import React, { FC } from 'react';
import styled from 'styled-components';
import {
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import { HormoneCalibrationData } from '../../../common/model/dto/lot-calibration-data';
import OvCalibrationTruthTable from './OvCalibrationTruthTable';
import OvCalibrationTruthTableChart from './OvCalibrationTruthTableChart';
import OvCalibrationCandlestickChart from './OvCalibrationCandlestickChart';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { useTranslation } from 'react-i18next';

interface OvHormoneCalibrationCardProps {
  hormone: 'lh' | 'pg' | 'lr_e3g';
  data: HormoneCalibrationData;
  unit: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`calibration-tabpanel-${index}`}
      aria-labelledby={`calibration-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const OvHormoneCalibrationCard: FC<OvHormoneCalibrationCardProps> = ({
  hormone,
  data,
  unit,
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = React.useState(0);

  const hormoneLabels = {
    lh: t('common.luteinizing'),
    pg: t('common.progesterone'),
    lr_e3g: t('common.lowRangeEstrogen'),
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (!data || !data.truthTable || data.truthTable.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">{hormoneLabels[hormone]} Calibration</Typography>
          <Typography color="text.secondary">
            {t('lots.calibration.noDataAvailable')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <StyledCard>
      <CardContent>
        <Title>{hormoneLabels[hormone]} {t('lots.calibration.title')}</Title>
        
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label={t('lots.calibration.tabs.chart')} />
          <Tab label={t('lots.calibration.tabs.candlestick')} />
          <Tab label={t('lots.calibration.tabs.table')} />
        </Tabs>

        <TabPanel value={selectedTab} index={0}>
          <OvCalibrationTruthTableChart
            data={data.truthTable}
            thresholds={data.thresholds}
            unit={unit}
          />
        </TabPanel>

        <TabPanel value={selectedTab} index={1}>
          {data.errorMargin && data.errorMargin > 0 ? (
            <OvCalibrationCandlestickChart
              data={data.truthTable}
              thresholds={data.thresholds}
              unit={unit}
            />
          ) : (
            <Typography color="text.secondary">
              {t('lots.calibration.noErrorMarginData')}
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={selectedTab} index={2}>
          <OvCalibrationTruthTable
            data={data.truthTable}
            unit={unit}
          />
        </TabPanel>
      </CardContent>
    </StyledCard>
  );
};

export default OvHormoneCalibrationCard;

const StyledCard = styled(Card)`
  && {
    margin-bottom: 1.5rem;
    box-shadow: ${Variables.boxShadow.defaultBox};
    border-radius: ${Variables.borderRadius.SMALL};
  }
`;

const Title = styled(Typography).attrs({
  variant: 'h6',
})`
  && {
    margin-bottom: 1rem;
    color: ${Colours.OV_BASE};
    font-weight: bold;
  }
`;

